// StaffCountBody — the "Request from HQ" (monthly count) section. Staff
// pick their outreach team, pick a month, enter start/end counts and
// hit Save. Below-min saves auto-generate a pending admin request, and
// a subsequent save on the same row surfaces the dedup confirm modal.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, ArrowDownCircle, Check, ChevronDown, ChevronUp, X } from "lucide-react";
import { AMBER, NAVY, TEAL, useInv } from "./constants";
import { SEED_CATALOGUE, SEED_LAST_MONTH_START, SEED_REQUESTS, SEED_SUPPLIERS, SEED_TEAMS, SEED_TEAM_STOCK, teamLabel, today } from "./seed";
import { SortHeader } from "./ui";

export function StaffSurface() {
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-5 sm:p-6 shadow-sm">
      <div className="mb-4">
        <p className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-1">Staff · /inventory · {today}</p>
        <h2 className="text-xl sm:text-2xl font-semibold tracking-tight" style={{ color: NAVY }}>
          Request from HQ
        </h2>
      </div>
      <StaffCountBody />
    </section>
  );
}

export function StaffCountBody() {
  const {
    catalogue: CATALOGUE = SEED_CATALOGUE,
    suppliers: SUPPLIERS = SEED_SUPPLIERS,
    teams: TEAMS = SEED_TEAMS,
    teamStock: TEAM_STOCK = SEED_TEAM_STOCK,
    cycles: CYCLES = [],
    loaded,
    requests: REQUESTS = SEED_REQUESTS,
    lastMonthStart: LAST_MONTH_START = SEED_LAST_MONTH_START,
    submitCycle,
    submitRequest,
    cancelRequest,
    activeTeam,
    setActiveTeam,
  } = useInv();
  const [team, setTeam] = useState(activeTeam || "outreach1");
  // Keep the store's activeTeam in sync so admin views below can filter.
  useEffect(() => {
    if (activeTeam && activeTeam !== team) setTeam(activeTeam);
  }, [activeTeam, team]);
  // Month picker — defaults to the current calendar month, offers the last 6
  // months and the next 3 so teams can back-fill or plan ahead.
  const months = useMemo(() => {
    const list = [];
    const now = new Date();
    for (let offset = 6; offset >= -3; offset--) {
      const d = new Date(now.getFullYear(), now.getMonth() - offset, 1);
      // Short-month format ("Aug 2026") keeps the pill compact enough to sit
      // next to the Outreach buttons on narrow phones.
      list.push(d.toLocaleString(undefined, { month: "short", year: "numeric" }));
    }
    return list;
  }, []);
  const [month, setMonth]   = useState(() =>
    new Date().toLocaleString(undefined, { month: "short", year: "numeric" })
  );
  // START count: the CURRENT month preloads from the team's live on-hand
  // stock (what staff expect while counting); every other month shows
  // only what was explicitly saved for it — blank otherwise. END count
  // preloads from the SAVED cycle for the selected team + month. Items
  // with no saved count stay empty ("") — that empty-vs-number
  // distinction is what tells us whether an order should fire on Save.
  // Backend stores UNITS; the UI works in PACKS (floor on convert).
  // `loadedFor` makes sure we apply the sheet ONCE per (team, month)
  // selection — a row Save triggers a config refresh which would
  // otherwise wipe numbers the user is still typing on other rows.
  const [startCounts, setStartCounts] = useState({});
  const [endCounts,   setEndCounts]   = useState({});
  const [saveState, setSaveState] = useState({ kind: "idle", msg: "" });
  // Per-row save state so each item's button can show its own progress
  // without stepping on the other rows' saves.
  const [rowSave, setRowSave] = useState({}); // { [itemId]: "saving" | "ok" | "err" | undefined }
  // Manual order-quantity overrides — staff can step the order qty down
  // (or up) from the auto-calculated "needed" amount before tapping
  // Order. { [itemId]: number }; undefined = use the computed needed.
  const [orderQty, setOrderQty] = useState({});
  const loadedFor = useRef("");

  useEffect(() => {
    if (!loaded) return; // wait for the real config, not the seed fallback
    const key = `${team}|${month}`;
    if (loadedFor.current === key) return;
    loadedFor.current = key;
    const cycleItems = (phase) =>
      (CYCLES || []).find((c) => c.team === team && c.month === month && c.phase === phase)?.items || {};
    const startCy = cycleItems("start");
    const endCy   = cycleItems("end");
    // CURRENT month: Start preloads from the team's live on-hand stock
    // (the behaviour staff expect while actively counting). Any OTHER
    // month shows only what was explicitly saved for that month — blank
    // ("") otherwise, so Sept/Oct never inherit August's counts.
    const currentMonth = new Date().toLocaleString(undefined, { month: "short", year: "numeric" });
    const isCurrent = month === currentMonth;
    setStartCounts(Object.fromEntries(CATALOGUE.map((c) => {
      const pq = Number(c.packQty) || 1;
      if (!isCurrent) {
        return [c.id, startCy[c.id] !== undefined ? Math.floor(startCy[c.id] / pq) : ""];
      }
      const units = TEAM_STOCK[team]?.[c.id] ?? 0;
      return [c.id, Math.floor(units / pq)];
    })));
    setEndCounts(Object.fromEntries(CATALOGUE.map((c) => {
      const pq = Number(c.packQty) || 1;
      return [c.id, endCy[c.id] !== undefined ? Math.floor(endCy[c.id] / pq) : ""];
    })));
    setRowSave({});
    setOrderQty({});
  }, [team, month, loaded, CYCLES, CATALOGUE, TEAM_STOCK]);

  const switchTeam = (t) => {
    setTeam(t);
    if (setActiveTeam) setActiveTeam(t);
  };

  const rows = useMemo(() => CATALOGUE.map((it) => {
    // NOTE: startCounts / endCounts store values in PACKS (that's what
    // the user enters). We convert to units only when persisting to the
    // backend (submitCycle body).
    const pq        = Number(it.packQty) || 1;
    const rawStart  = startCounts[it.id];
    // Blank-aware like endCounts: "" means "nothing saved/entered" and
    // renders as an empty field (months other than the current one
    // start blank until staff fill them in).
    const startEntered = rawStart !== "" && rawStart !== undefined && rawStart !== null;
    const start     = startEntered ? Number(rawStart) : 0;
    const rawEnd    = endCounts[it.id];
    // "Entered" means the field currently holds a number the user typed —
    // empty string / undefined mean the row is still awaiting its end count.
    const endEntered = rawEnd !== "" && rawEnd !== undefined && rawEnd !== null;
    const end       = endEntered ? Number(rawEnd) : 0;
    // consumed / needed / belowMin all live in PACKS (same currency as
    // start/end/min/max on this row).
    const consumed  = endEntered ? Math.max(0, start - end) : 0;
    const needed    = endEntered ? Math.max((it.max || 0) - end, 0) : 0;
    const belowMin  = endEntered && end < (it.min || 0);
    // Convenience for the tooltip / footer conversions.
    const endUnits  = end * pq;
    return { ...it, start, startEntered, end, endUnits, endEntered, consumed, needed, belowMin };
  }), [startCounts, endCounts, CATALOGUE]);

  // Effective order quantity for a row — the staff-adjusted override when
  // set, otherwise the auto-calculated "needed". Steppers clamp 1–99.
  const effOrderQty = (r) => Math.max(1, orderQty[r.id] ?? r.needed);
  const stepOrderQty = (r, delta) =>
    setOrderQty((q) => {
      const cur = q[r.id] ?? r.needed;
      const next = Math.min(99, Math.max(1, cur + delta));
      return { ...q, [r.id]: next };
    });

  // Supplier lookup for display + sorting.
  const supplierName = useCallback(
    (sid) => SUPPLIERS.find((s) => s.id === sid)?.company || "—",
    [SUPPLIERS]
  );

  // Sort state — default: supplier ascending (groups items by supplier).
  const [sortBy, setSortBy] = useState({ col: "supplier", dir: "asc" });
  const toggleSort = (col) => setSortBy((s) =>
    s.col === col ? { col, dir: s.dir === "asc" ? "desc" : "asc" } : { col, dir: "asc" }
  );
  const sortedRows = useMemo(() => {
    const arr = rows.map((r) => ({ ...r, supplierName: supplierName(r.supplier) }));
    const cmp = (a, b) => {
      const dir = sortBy.dir === "asc" ? 1 : -1;
      const key = sortBy.col;
      const av = (a[key] || "").toString().toLowerCase();
      const bv = (b[key] || "").toString().toLowerCase();
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      // Secondary sort by name so items under the same supplier are alpha.
      if (a.name < b.name) return -1;
      if (a.name > b.name) return 1;
      return 0;
    };
    return arr.sort(cmp);
  }, [rows, sortBy, supplierName]);

  // Category filter — pill row above the team/month bar. "All" is the
  // default; each unique category present in the catalogue gets its own
  // pill. Items without a category are grouped under "Uncategorized".
  const [categoryFilter, setCategoryFilter] = useState("all");
  const categoryPills = useMemo(() => {
    const set = new Set();
    let hasUncategorized = false;
    for (const c of CATALOGUE) {
      const cat = (c.category || "").trim();
      if (cat) set.add(cat); else hasUncategorized = true;
    }
    const list = Array.from(set).sort((a, b) => a.localeCompare(b));
    if (hasUncategorized) list.push("__uncat__");
    return list;
  }, [CATALOGUE]);
  const filteredRows = useMemo(() => {
    if (categoryFilter === "all") return sortedRows;
    if (categoryFilter === "__uncat__") return sortedRows.filter((r) => !((r.category || "").trim()));
    return sortedRows.filter((r) => (r.category || "").trim() === categoryFilter);
  }, [sortedRows, categoryFilter]);

  // Pending-request lookup: existing pending row for this team + item.
  const pendingByItem = useMemo(() => {
    const m = {};
    for (const r of REQUESTS) {
      if (r.status === "pending" && r.team === team) m[r.item] = r;
    }
    return m;
  }, [REQUESTS, team]);

  // Row-level "block & inform" confirm state. Null = no confirm open.
  // Shape: { rowId, existingQty, newNeeded }
  const [pendingConfirm, setPendingConfirm] = useState(null);

  return (
    <>
      {/* Category filter — a compact <select> on mobile so it doesn't
          wrap into a messy multi-line pill soup, and a pill row on
          tablet/desktop where there's room. Both bind to the same
          categoryFilter state. */}
      {categoryPills.length > 0 && (
        <div className="mb-2" data-testid="staff-category-filter">
          {/* Mobile — single-line dropdown */}
          <label className="flex sm:hidden items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold shrink-0">Filter</span>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              data-testid="cat-filter-select"
              className="flex-1 h-9 rounded-md border border-slate-200 bg-white px-2 text-xs font-semibold"
              style={{ color: NAVY }}
            >
              <option value="all">All categories</option>
              {categoryPills.map((c) => (
                <option key={c} value={c}>
                  {c === "__uncat__" ? "Uncategorized" : c}
                </option>
              ))}
            </select>
          </label>
          {/* Tablet / desktop — pill row */}
          <div className="hidden sm:flex flex-wrap items-center gap-1.5">
            <span className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold mr-1">Filter</span>
            <button
              type="button"
              onClick={() => setCategoryFilter("all")}
              data-testid="cat-filter-all"
              className={`h-7 px-3 rounded-full text-xs font-semibold border transition ${categoryFilter === "all" ? "text-white border-transparent" : "text-slate-600 border-slate-200 bg-white hover:bg-slate-50"}`}
              style={categoryFilter === "all" ? { background: NAVY } : {}}
            >
              All
            </button>
            {categoryPills.map((c) => {
              const label = c === "__uncat__" ? "Uncategorized" : c;
              const active = categoryFilter === c;
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCategoryFilter(c)}
                  data-testid={`cat-filter-${label}`}
                  className={`h-7 px-3 rounded-full text-xs font-semibold border transition ${active ? "text-white border-transparent" : "text-slate-600 border-slate-200 bg-white hover:bg-slate-50"}`}
                  style={active ? { background: NAVY } : {}}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Team + Month selector — one connected pill group. On desktop the
          buttons share the full width evenly (right edge lines up with the
          "Monthly count" tab above). On mobile they keep their natural
          width and the strip scrolls sideways so nothing gets squished. */}
      <div className="mb-3">
        <div className="flex items-stretch rounded-md border border-slate-200 bg-white w-full overflow-x-auto">
          {TEAMS.map((t) => {
            const active = t.key === team;
            return (
              <button key={t.key} onClick={() => switchTeam(t.key)}
                className={`flex-none sm:flex-1 h-9 px-3 text-xs font-semibold whitespace-nowrap border-r border-slate-200 ${active ? "text-white" : "text-slate-600 hover:bg-slate-50"}`}
                style={active ? { background: NAVY } : {}}>
                {t.label}
              </button>
            );
          })}
          {/* Month dropdown fused to the right of the outreach buttons. */}
          <select
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            data-testid="count-month-select"
            className="flex-none sm:flex-1 min-w-[8.5rem] h-9 px-8 text-xs font-semibold text-white appearance-none cursor-pointer bg-no-repeat text-center"
            style={{
              background: `${NAVY} url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='white'%3E%3Cpath fill-rule='evenodd' d='M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.06l3.71-3.83a.75.75 0 1 1 1.08 1.04l-4.25 4.38a.75.75 0 0 1-1.08 0L5.21 8.27a.75.75 0 0 1 .02-1.06z' clip-rule='evenodd'/%3E%3C/svg%3E") no-repeat right 0.5rem center / 1rem`,
            }}>
            {months.map((m) => (
              <option key={m} value={m} className="text-black">{m}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Explanatory hint */}
      <div className="mb-4 rounded-md border p-3 text-xs" style={{ borderColor: `${TEAL}55`, background: `${TEAL}0F`, color: NAVY }}>
        <p className="font-semibold" style={{ color: TEAL }}>
          Enter what {teamLabel(team, TEAMS)} has on hand for {month}.
        </p>
        <p className="mt-0.5 text-slate-600">
          Fill the <b>Start count</b> at the beginning of the month and tap <b>Save</b> — this only writes the count, it never orders anything. Come back at the end of the month, enter the <b>End count</b>, tap <b>Save</b>, and then tap <b>Order</b> on any row that dropped below its minimum to push a request to HQ.
        </p>
      </div>

      {/* Count table — one row per item with BOTH Start count and End count
          columns visible at the same time. All numeric columns are
          center-aligned so the values sit centred under their headers. */}
      <div className="overflow-x-auto -mx-5 sm:-mx-6 px-5 sm:px-6">
        <table className="w-full min-w-[720px] text-sm [&_th]:whitespace-nowrap [&_td]:whitespace-nowrap">
          <thead>
            <tr className="text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <th className="py-2.5 pr-4 font-semibold text-left">
                <SortHeader label="Item" col="name" sortBy={sortBy} onClick={() => toggleSort("name")} />
              </th>
              <th className="py-2.5 pr-4 font-semibold text-center">Units</th>
              <th className="py-2.5 pr-4 font-semibold text-center">Min / Max</th>
              <th className="py-2.5 pr-4 font-semibold text-center">Start count</th>
              <th className="py-2.5 pr-4 font-semibold text-center">End count</th>
              <th className="py-2.5 pr-4 font-semibold text-center">Consumed</th>
              <th className="py-2.5 pr-4 font-semibold text-center">Needed</th>
              <th className="py-2.5 pr-4 font-semibold text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.length === 0 && (
              <tr>
                <td colSpan={8} className="py-10 text-center text-sm text-slate-500 italic">
                  {sortedRows.length === 0 ? (
                    <>
                      No items in the catalogue yet.<br />
                      Add suppliers &amp; items in the <b>Suppliers &amp; items</b> section below,<br />
                      then come back to enter this team&apos;s starting counts.
                    </>
                  ) : (
                    <>No items in this category. Pick another filter above.</>
                  )}
                </td>
              </tr>
            )}
            {filteredRows.map((r) => {
              const pendingReq = pendingByItem[r.id];
              return (
              <tr key={r.id} className="border-b border-slate-100 hover:bg-[#F0F5F4]/60 transition">
                <td className="py-3 pr-4 font-mono text-xs text-slate-600 text-left">{r.name}</td>
                <td className="py-3 pr-4 text-center">
                  <span className="inline-flex items-center justify-center h-6 px-2 rounded-md text-xs font-semibold bg-slate-100 text-slate-700 tabular-nums">
                    {r.packQty}
                  </span>
                </td>
                <td className="py-3 pr-4 text-center tabular-nums text-slate-500">{r.min} / {r.max}</td>
                <td className="py-3 pr-4 text-center">
                  <input type="number" value={r.startEntered ? r.start : ""} min="0" inputMode="numeric" placeholder="0"
                    data-testid={`start-count-input-${r.id}`}
                    onChange={(e) => {
                      const v = e.target.value;
                      setStartCounts((c) => ({ ...c, [r.id]: v === "" ? "" : Number(v) }));
                    }}
                    className="h-9 w-20 text-center rounded-md border border-slate-300 focus:border-[#2A9F92] focus:ring-2 focus:ring-[#2A9F92]/40 tabular-nums font-semibold"
                    style={{ color: NAVY, fontSize: 16 }} />
                </td>
                <td className="py-3 pr-4 text-center">
                  <input type="number" value={r.endEntered ? r.end : ""} min="0" inputMode="numeric" placeholder="0"
                    data-testid={`end-count-input-${r.id}`}
                    onChange={(e) => {
                      const v = e.target.value;
                      setEndCounts((c) => ({ ...c, [r.id]: v === "" ? "" : Number(v) }));
                    }}
                    className="h-9 w-20 text-center rounded-md border border-slate-300 focus:border-[#2A9F92] focus:ring-2 focus:ring-[#2A9F92]/40 tabular-nums font-semibold"
                    style={{ color: NAVY, fontSize: 16 }} />
                </td>
                <td className="py-3 pr-4 text-center tabular-nums">
                  {r.endEntered ? (
                    <span className="inline-flex items-center gap-1 h-7 px-2 rounded-md text-xs font-semibold" style={{ color: TEAL, background: `${TEAL}14` }}>
                      <ArrowDownCircle className="h-3.5 w-3.5" />
                      {r.consumed}
                    </span>
                  ) : (
                    <span className="text-xs text-slate-400">—</span>
                  )}
                </td>
                <td className="py-3 pr-4 text-center tabular-nums font-semibold">
                  {!r.endEntered ? (
                    <span className="text-xs text-slate-400">—</span>
                  ) : (
                    <span className="inline-flex items-center gap-1">
                      {/* Down/up steppers let staff order LESS (or more)
                          than the full top-up — e.g. when supplies are
                          running low they may only want a partial order.
                          The slots are ALWAYS rendered (invisible at zero)
                          so the quantity pill never shifts column. */}
                      <button
                        type="button"
                        onClick={() => stepOrderQty(r, -1)}
                        disabled={r.needed <= 0 || effOrderQty(r) <= 1}
                        data-testid={`order-qty-down-${r.id}`}
                        title="Order one less"
                        style={{ visibility: r.needed > 0 ? "visible" : "hidden" }}
                        className="inline-flex items-center justify-center h-7 w-7 rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed">
                        <ChevronDown className="h-4 w-4" />
                      </button>
                      {r.needed > 0 ? (
                        <span
                          className={`inline-flex items-center gap-1 h-7 px-2 rounded-md text-xs ${r.belowMin ? "bg-red-50 text-red-600 border border-red-200" : ""}`}
                          style={r.belowMin ? {} : { color: TEAL, background: `${TEAL}14` }}
                          title={orderQty[r.id] !== undefined && orderQty[r.id] !== r.needed
                            ? `Full top-up is ${r.needed} — ordering ${effOrderQty(r)}`
                            : `Order qty (full top-up to max)`}>
                          {r.belowMin && <AlertTriangle className="h-3.5 w-3.5" />}
                          {effOrderQty(r)}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 h-7 px-2 rounded-md text-xs" style={{ color: TEAL, background: `${TEAL}14` }}>
                          {r.needed}
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => stepOrderQty(r, +1)}
                        disabled={r.needed <= 0}
                        data-testid={`order-qty-up-${r.id}`}
                        title="Order one more"
                        style={{ visibility: r.needed > 0 ? "visible" : "hidden" }}
                        className="inline-flex items-center justify-center h-7 w-7 rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50">
                        <ChevronUp className="h-4 w-4" />
                      </button>
                    </span>
                  )}
                </td>
                <td className="py-3 pr-4 text-center">
                  <div className="inline-flex items-center gap-2">
                  <button
                    onClick={async () => {
                      if (!submitCycle) return;
                      setRowSave((s) => ({ ...s, [r.id]: "saving" }));
                      try {
                        // Save persists counts ONLY. No auto-order is
                        // triggered — staff have to click the Order
                        // button explicitly to push a request to HQ.
                        const pq = Number(r.packQty) || 1;
                        // Only persist a start count staff actually see
                        // in the field — a blank start (future months)
                        // must not write a 0 and snap live stock down.
                        // Non-current months never move live stock.
                        const isCurrentMonth = month === new Date().toLocaleString(undefined, { month: "short", year: "numeric" });
                        if (r.startEntered) {
                          await submitCycle({ team, month, phase: "start", items: { [r.id]: r.start * pq }, autoOrder: false, snapStock: isCurrentMonth });
                        }
                        if (r.endEntered) {
                          await submitCycle({ team, month, phase: "end", items: { [r.id]: r.end * pq }, autoOrder: false, snapStock: isCurrentMonth });
                        }
                        setRowSave((s) => ({ ...s, [r.id]: "ok" }));
                        setTimeout(() => setRowSave((s) => {
                          if (s[r.id] !== "ok") return s;
                          const { [r.id]: _drop, ...rest } = s;
                          return rest;
                        }), 2000);
                      } catch (e) {
                        setRowSave((s) => ({ ...s, [r.id]: "err" }));
                        alert(e.message || "Could not save");
                      }
                    }}
                    disabled={!submitCycle || rowSave[r.id] === "saving"}
                    data-testid={`row-save-btn-${r.id}`}
                    className="inline-flex items-center justify-center gap-1 h-8 w-[76px] rounded-md text-xs font-semibold text-white shadow-sm disabled:opacity-60"
                    style={{ background: rowSave[r.id] === "ok" ? TEAL : NAVY }}>
                    {rowSave[r.id] === "saving" ? "Saving…" :
                     rowSave[r.id] === "ok"     ? (<><Check className="h-3.5 w-3.5" /> Saved</>) :
                                                   "Save"}
                  </button>
                  {/* Order — separate, explicit "push order to HQ" click.
                      Only enabled when the row is below its team minimum
                      AND has an end count entered. Blocks with a confirm
                      modal when a pending request already exists so we
                      never queue two rows for the same item. */}
                  <button
                    onClick={async () => {
                      if (!submitRequest) return;
                      // Existing pending → confirm-then-replace flow.
                      if (pendingReq) {
                        setPendingConfirm({
                          rowId: r.id,
                          itemName: r.name,
                          existingQty: pendingReq.qty,
                          newNeeded: effOrderQty(r),
                        });
                        return;
                      }
                      setRowSave((s) => ({ ...s, [r.id]: "saving" }));
                      try {
                        await submitRequest({ team, item: r.id, qty: effOrderQty(r) });
                        setRowSave((s) => ({ ...s, [r.id]: "ok" }));
                        setTimeout(() => setRowSave((s) => {
                          if (s[r.id] !== "ok") return s;
                          const { [r.id]: _drop, ...rest } = s;
                          return rest;
                        }), 2000);
                      } catch (e) {
                        setRowSave((s) => ({ ...s, [r.id]: "err" }));
                        alert(e.message || "Could not order");
                      }
                    }}
                    disabled={
                      !submitRequest ||
                      rowSave[r.id] === "saving" ||
                      !r.endEntered ||
                      !(r.needed > 0)
                    }
                    data-testid={`row-order-btn-${r.id}`}
                    title={
                      !r.endEntered ? "Enter the end count first" :
                      !(r.needed > 0) ? "Stock is already at max — no order needed" :
                      pendingReq    ? `Replace the existing pending order (qty ${pendingReq.qty})` :
                                      `Order ${effOrderQty(r)} pack${effOrderQty(r) === 1 ? "" : "s"} of ${r.name}`
                    }
                    className="inline-flex items-center justify-center gap-1 h-8 w-[76px] rounded-md text-xs font-semibold border shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{
                      borderColor: (!r.endEntered || !(r.needed > 0)) ? "#E5E7EB" : AMBER,
                      color:       (!r.endEntered || !(r.needed > 0)) ? "#9CA3AF" : "#78350F",
                      background:  (!r.endEntered || !(r.needed > 0)) ? "#F9FAFB" : `${AMBER}1A`,
                    }}>
                    Order
                  </button>
                  {/* Cancellation buddy — always rendered so the Save
                      button stays in a fixed column. When there is no
                      pending auto-request the pill is greyed out and
                      not clickable, keeping the row layout consistent
                      across every item. */}
                  <button
                    onClick={async () => {
                      if (!cancelRequest || !pendingReq) return;
                      if (!window.confirm(`Cancel the pending order for ${r.name} (qty ${pendingReq.qty})? This can't be undone.`)) return;
                      setRowSave((s) => ({ ...s, [r.id]: "saving" }));
                      try {
                        await cancelRequest(pendingReq.id);
                        setRowSave((s) => {
                          const { [r.id]: _drop, ...rest } = s;
                          return rest;
                        });
                      } catch (e) {
                        setRowSave((s) => ({ ...s, [r.id]: "err" }));
                        alert(e.message || "Could not cancel");
                      }
                    }}
                    disabled={!pendingReq || !cancelRequest || rowSave[r.id] === "saving"}
                    data-testid={`row-cancel-btn-${r.id}`}
                    title={pendingReq ? "Cancel this pending order" : "No pending order to cancel"}
                    className="inline-flex items-center justify-center gap-1 h-8 w-[86px] rounded-md text-xs font-semibold border shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{
                      borderColor: pendingReq ? "#FCA5A5" : "#E5E7EB",
                      color:       pendingReq ? "#B91C1C" : "#9CA3AF",
                      background:  pendingReq ? "#FEF2F2" : "#F9FAFB",
                    }}>
                    <X className="h-3.5 w-3.5" /> Cancel
                  </button>
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Block-and-inform confirm — modal overlay so it always sits in the
          viewport (avoids being missed below the horizontally-scrolled
          table on mobile). Click backdrop to cancel. */}
      {pendingConfirm && (
        <div
          className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center bg-slate-900/60 p-3 sm:p-6"
          onClick={() => setPendingConfirm(null)}
          data-testid="pending-dup-confirm">
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full sm:max-w-md bg-white rounded-xl shadow-2xl overflow-hidden border-2"
            style={{ borderColor: AMBER }}>
            <div className="p-5" style={{ background: `${AMBER}12` }}>
              <p className="text-[11px] uppercase tracking-[0.14em] font-semibold" style={{ color: "#78350F" }}>Already pending</p>
              <p className="text-sm font-semibold mt-1" style={{ color: NAVY }}>
                {teamLabel(team, TEAMS)} already has a pending order for {pendingConfirm.itemName}
                {" "}(qty <span className="tabular-nums">{pendingConfirm.existingQty}</span>).
              </p>
              <p className="text-xs text-slate-600 mt-2">
                Ordering now will <b>replace</b> the pending qty with your current order quantity
                (<span className="tabular-nums">{pendingConfirm.newNeeded}</span>) so the queue stays a single
                row — not a duplicate.
              </p>
            </div>
            <div className="px-5 py-3 flex justify-end gap-2 flex-wrap border-t border-slate-100 bg-white">
              <button
                onClick={() => setPendingConfirm(null)}
                data-testid="pending-dup-cancel-btn"
                className="h-9 px-4 rounded-md text-xs font-semibold border border-slate-300 text-slate-600 hover:bg-slate-50">
                Cancel
              </button>
              <button
                onClick={async () => {
                  const rid = pendingConfirm.rowId;
                  const qty = pendingConfirm.newNeeded;
                  setPendingConfirm(null);
                  if (!submitRequest) return;
                  const existing = pendingByItem[rid];
                  setRowSave((s) => ({ ...s, [rid]: "saving" }));
                  try {
                    // Cancel the existing pending row first, then create a
                    // fresh one at the staff-adjusted qty — keeps the queue
                    // to a single row per (team, item).
                    if (existing && cancelRequest) {
                      try { await cancelRequest(existing.id); } catch { /* best-effort */ }
                    }
                    await submitRequest({ team, item: rid, qty });
                    setRowSave((s) => ({ ...s, [rid]: "ok" }));
                    setTimeout(() => setRowSave((s) => {
                      if (s[rid] !== "ok") return s;
                      const { [rid]: _drop, ...rest } = s;
                      return rest;
                    }), 2000);
                  } catch (e) {
                    setRowSave((s) => ({ ...s, [rid]: "err" }));
                    alert(e.message || "Could not order");
                  }
                }}
                data-testid="pending-dup-replace-btn"
                className="h-9 px-5 rounded-md text-xs font-semibold text-white shadow-sm"
                style={{ background: TEAL }}>
                Replace pending
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mt-4 text-xs text-slate-500">
        Tap <b>Save</b> at the end of any row to update both Start and End counts for that item. Saving updates {teamLabel(team, TEAMS)}&apos;s live inventory for {month}.
      </div>
      {saveState.kind === "ok" && (
        <div className="mt-3 rounded-md border-2 p-3 flex items-center gap-2" style={{ borderColor: TEAL, background: `${TEAL}12`, color: TEAL }} data-testid="submit-cycle-ok">
          <Check className="h-5 w-5 shrink-0" />
          <div>
            <p className="text-sm font-semibold">Saved.</p>
            <p className="text-xs">{saveState.msg}</p>
          </div>
        </div>
      )}
      {saveState.kind === "err" && (
        <div className="mt-3 rounded-md border-2 border-red-300 bg-red-50 p-3 flex items-center gap-2 text-red-700" data-testid="submit-cycle-err">
          <AlertTriangle className="h-5 w-5 shrink-0" />
          <div>
            <p className="text-sm font-semibold">Could not submit</p>
            <p className="text-xs">{saveState.msg}</p>
          </div>
        </div>
      )}
    </>
  );
}
